// src/app/core/pwa/services/pwa-trigger.service.ts

import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable, fromEvent, timer } from 'rxjs';
import { filter, debounceTime, takeUntil } from 'rxjs/operators';
import { PwaService } from './pwa.service';

@Injectable({
  providedIn: 'root'
})
export class PwaTriggerService {
  private readonly MIN_SESSION_TIME = 10000; // 10 segundos
  private readonly MIN_INTERACTIONS = 3; // 3 interações
  private readonly INTERACTION_EVENTS = ['click', 'scroll', 'keydown'];

  private sessionStartTime = Date.now();
  private interactionCount = 0;
  private hasShownInSession = false;
  private isUserLoggedIn = false;
  private destroy$ = new BehaviorSubject<void>(undefined);

  constructor(
    private pwaService: PwaService,
    private router: Router
  ) {
    // Não inicializar automaticamente, aguardar chamada explícita
    console.log('PWA Trigger Service: Constructor called');
  }

  /**
   * Método público para inicializar o serviço
   */
  public initialize(): void {
    console.log('PWA Trigger Service: Initializing...');
    this.initTriggerLogic();
  }

  /**
   * Inicializa a lógica de trigger
   */
  private initTriggerLogic(): void {
    console.log('PWA Trigger Service: Setting up trigger logic');
    this.setupInteractionTracking();
    this.setupNavigationTracking();
    this.setupSessionTimer();
  }

  /**
   * Configura o rastreamento de interações do usuário
   */
  private setupInteractionTracking(): void {
    this.INTERACTION_EVENTS.forEach(eventType => {
      fromEvent(document, eventType)
        .pipe(
          debounceTime(1000), // Evita spam de eventos
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.onUserInteraction();
        });
    });
  }

  /**
   * Configura o rastreamento de navegação
   */
  private setupNavigationTracking(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.onPageNavigation(event.url);
      });
  }

  /**
   * Configura o timer de sessão
   */
  private setupSessionTimer(): void {
    timer(this.MIN_SESSION_TIME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkTriggerConditions();
      });
  }

  /**
   * Manipula interações do usuário
   */
  private onUserInteraction(): void {
    this.interactionCount++;
    
    // Verifica se deve mostrar o popup após interações suficientes
    if (this.interactionCount >= this.MIN_INTERACTIONS) {
      this.checkTriggerConditions();
    }
  }

  /**
   * Manipula navegação entre páginas
   */
  private onPageNavigation(url: string): void {
    // Marca como logado se chegou ao menu ou lista de clientes
    if (url.includes('/menu') || url.includes('/lista-clientes')) {
      this.isUserLoggedIn = true;
    }

    // Reset do contador de interações em páginas principais
    if (url.includes('/menu')) {
      this.interactionCount = 0;
    }
  }

  /**
   * Verifica se deve mostrar o popup
   */
  private checkTriggerConditions(): void {
    console.log('PWA Trigger: Checking trigger conditions...');
    
    if (this.hasShownInSession) {
      console.log('PWA Trigger: Already shown in this session');
      return; // Já mostrou nesta sessão
    }

    const sessionTime = Date.now() - this.sessionStartTime;
    const hasMinimumTime = sessionTime >= this.MIN_SESSION_TIME;
    const hasMinimumInteractions = this.interactionCount >= this.MIN_INTERACTIONS;
    const canShowPrompt = this.pwaService.canShowPrompt();

    console.log('PWA Trigger: Conditions check:', {
      sessionTime: sessionTime,
      hasMinimumTime: hasMinimumTime,
      interactionCount: this.interactionCount,
      hasMinimumInteractions: hasMinimumInteractions,
      canShowPrompt: canShowPrompt
    });

    // Removido a exigência de login - popup pode aparecer em qualquer tela
    const shouldTrigger = 
      (hasMinimumTime || hasMinimumInteractions) &&
      canShowPrompt;

    console.log('PWA Trigger: Should trigger?', shouldTrigger);

    if (shouldTrigger) {
      this.triggerPrompt();
    }
  }

  /**
   * Dispara o popup de instalação
   */
  private triggerPrompt(): void {
    this.hasShownInSession = true;
    
    // Pequeno delay para não interromper a navegação
    setTimeout(() => {
      this.pwaService.showPromptUI();
      console.log('PWA: Install prompt triggered');
    }, 1000);
  }

  /**
   * Força a verificação das condições (útil para testes)
   */
  public forceCheck(): void {
    this.checkTriggerConditions();
  }

  /**
   * Reseta o estado da sessão
   */
  public resetSession(): void {
    this.sessionStartTime = Date.now();
    this.interactionCount = 0;
    this.hasShownInSession = false;
    this.isUserLoggedIn = false;
  }

  /**
   * Marca o usuário como logado (para uso manual)
   */
  public setUserLoggedIn(loggedIn: boolean): void {
    this.isUserLoggedIn = loggedIn;
    if (loggedIn) {
      this.checkTriggerConditions();
    }
  }

  /**
   * Obtém estatísticas da sessão atual
   */
  public getSessionStats(): {
    sessionTime: number;
    interactionCount: number;
    isLoggedIn: boolean;
    hasShownInSession: boolean;
  } {
    return {
      sessionTime: Date.now() - this.sessionStartTime,
      interactionCount: this.interactionCount,
      isLoggedIn: this.isUserLoggedIn,
      hasShownInSession: this.hasShownInSession
    };
  }

  /**
   * Cleanup quando o serviço é destruído
   */
  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
